import {
  type AuthRegisterCredentialsT,
  type AuthLoginCredentialsT,
  type AuthConfirmResetPasswordT,
} from '@ruminati/types_v2/auth'
import { type UserWithOrganisationsAndRelationshipsT } from '@ruminati/types_v2/user'
import { HTTPError } from 'ky'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { CommonApiV2Instance } from '@/services/common'
import { queryClient } from '@/services/queryClient'
import {
  sendBroadcastMessageFromTabIfFocused,
  setHasActiveSession,
  SESSION_CHANGE_REASONS,
} from '@/utilities/broadcaster'
import { rollbar } from '@/utilities/rollbar'

import { useAgriwebbStore } from './agriwebb'
import { useEmissionsReportsStore } from './emissionsReports'
import { useLivestockStore } from './livestock'
import { useNotificationsStore } from './notifications'
import { useOrganisationStore } from './organisations'
import { usePropertiesStore } from './properties'
import { useUiStore } from './ui'
import { useUsersStore } from './users'

type FetchResult<T> = { success: true; data: T } | { success: false; error: unknown }

async function parseAuthError(err: unknown): Promise<unknown> {
  if (err instanceof HTTPError) {
    const parsedErr = await err.response.json()
    return parsedErr.code
  }
  return err
}

export const useAuthStore = defineStore('auth', () => {
  const userStore = useUsersStore()
  const organisationStore = useOrganisationStore()

  setHasActiveSession(() => userStore.profile !== undefined)

  const registerWithCredentialsData = ref<UserWithOrganisationsAndRelationshipsT | undefined>()
  const isRegisteringWithCredentials = ref(false)
  const registerWithCredentialsError = ref<unknown>()

  async function registerWithCredentials(
    details: AuthRegisterCredentialsT,
  ): Promise<FetchResult<UserWithOrganisationsAndRelationshipsT>> {
    isRegisteringWithCredentials.value = true
    try {
      const response = await CommonApiV2Instance.post('auth/register', {
        json: details,
      }).json<UserWithOrganisationsAndRelationshipsT>()
      sendBroadcastMessageFromTabIfFocused(SESSION_CHANGE_REASONS.USER_CHANGED)
      registerWithCredentialsData.value = response
      registerWithCredentialsError.value = undefined
      return { success: true, data: response }
    } catch (err) {
      registerWithCredentialsError.value = await parseAuthError(err)
      return { success: false, error: err }
    } finally {
      isRegisteringWithCredentials.value = false
    }
  }

  const isLoggingInWithCredentials = ref(false)
  const loginWithCredentialsError = ref<unknown>()

  async function loginWithCredentials(
    details: AuthLoginCredentialsT,
  ): Promise<FetchResult<UserWithOrganisationsAndRelationshipsT>> {
    isLoggingInWithCredentials.value = true
    try {
      const response = await CommonApiV2Instance.post('auth/login', {
        json: details,
      }).json<UserWithOrganisationsAndRelationshipsT>()

      if (response.organisations !== undefined) {
        organisationStore.upsertOrganisations(response.organisations)
      }
      if (response.relationships !== undefined) {
        userStore.relationships = response.relationships
      }
      userStore.profile = response.user
      organisationStore.initializeCurrentOrg(response.user)
      queryClient.setQueryData(['profile'], response.user)
      sendBroadcastMessageFromTabIfFocused(SESSION_CHANGE_REASONS.USER_CHANGED)

      loginWithCredentialsError.value = undefined
      return { success: true, data: response }
    } catch (err) {
      loginWithCredentialsError.value = await parseAuthError(err)
      return { success: false, error: err }
    } finally {
      isLoggingInWithCredentials.value = false
    }
  }

  const isSendingVerificationEmail = ref(false)
  const sendVerificationEmailError = ref<unknown>()
  const lastAttemptedSendingVerificationEmail = ref<number>()

  async function sendVerificationEmail(details: { email: string }): Promise<FetchResult<void>> {
    isSendingVerificationEmail.value = true
    lastAttemptedSendingVerificationEmail.value = Date.now()
    try {
      await CommonApiV2Instance.post('auth/verify-email', {
        json: details,
        headers: { 'content-type': undefined },
      })
      sendVerificationEmailError.value = undefined
      return { success: true, data: undefined }
    } catch (err) {
      sendVerificationEmailError.value = await parseAuthError(err)
      return { success: false, error: err }
    } finally {
      isSendingVerificationEmail.value = false
    }
  }

  const isSendingForgotPasswordEmail = ref(false)
  const sendForgotPasswordEmailError = ref<unknown>()
  const lastAttemptedSendingForgotPasswordEmail = ref<number>()

  async function sendForgotPasswordEmail(details: { email: string }): Promise<FetchResult<void>> {
    isSendingForgotPasswordEmail.value = true
    lastAttemptedSendingForgotPasswordEmail.value = Date.now()
    try {
      await CommonApiV2Instance.post('auth/forgot-password', {
        json: details,
        headers: { 'content-type': undefined },
      })
      sendForgotPasswordEmailError.value = undefined
      return { success: true, data: undefined }
    } catch (err) {
      sendForgotPasswordEmailError.value = await parseAuthError(err)
      return { success: false, error: err }
    } finally {
      isSendingForgotPasswordEmail.value = false
    }
  }

  const isConfirmingResetPassword = ref(false)
  const confirmResetPasswordError = ref<unknown>()
  const lastAttemptedConfirmingResetPassword = ref<number>()

  async function confirmResetPassword(
    details: AuthConfirmResetPasswordT,
  ): Promise<FetchResult<void>> {
    isConfirmingResetPassword.value = true
    lastAttemptedConfirmingResetPassword.value = Date.now()
    try {
      await CommonApiV2Instance.post('auth/confirm-reset-password', {
        json: details,
        headers: { 'content-type': undefined },
      })
      confirmResetPasswordError.value = undefined
      return { success: true, data: undefined }
    } catch (err) {
      confirmResetPasswordError.value = await parseAuthError(err)
      return { success: false, error: err }
    } finally {
      isConfirmingResetPassword.value = false
    }
  }

  const isLoggingOut = ref(false)
  const logoutError = ref<unknown>()

  function resetUserScopedStores() {
    // logout resets profile, as well as other stores
    const usersStore = useUsersStore()
    // const clientsStore = usePartnerOrgStore()
    const propertiesStore = usePropertiesStore()
    // const reductionPlanStore = useReductionPlansStore()
    const emissionsReportsStore = useEmissionsReportsStore()
    const notificationsStore = useNotificationsStore()
    // const dataSharingStore = useDataSharingEmissionsOrgStore()
    // const orgMembersStore = useOrgMembersStore()
    // const partnersStore = usePartnersStore()
    const agriwebbStore = useAgriwebbStore()
    const livestockStore = useLivestockStore()
    const uiStore = useUiStore()

    // clear TanStack query cache so all queries refetch fresh on next login
    queryClient.clear()

    // clear other stores
    usersStore.reset() // also clears current user profile
    // clientsStore.reset()
    propertiesStore.resetAll()
    // reductionPlanStore.resetAll()
    emissionsReportsStore.resetAll()
    notificationsStore.reset()
    // dataSharingStore.reset()
    // orgMembersStore.reset()
    // partnersStore.reset()
    organisationStore.reset()
    agriwebbStore.reset()
    livestockStore.resetAll()

    uiStore.reset()

    // clear rollbar
    rollbar.configure({
      payload: {
        person: undefined,
      },
    })
  }

  async function logout(): Promise<FetchResult<void>> {
    isLoggingOut.value = true
    try {
      await CommonApiV2Instance.post('auth/logout', {
        headers: { 'content-type': undefined },
      })

      resetUserScopedStores()
      sendBroadcastMessageFromTabIfFocused(SESSION_CHANGE_REASONS.LOGGED_OUT)

      logoutError.value = undefined
      return { success: true, data: undefined }
    } catch (err) {
      logoutError.value = await parseAuthError(err)
      return { success: false, error: err }
    } finally {
      isLoggingOut.value = false
    }
  }

  return {
    registerWithCredentialsData,
    registerWithCredentials,
    isRegisteringWithCredentials,
    registerWithCredentialsError,

    loginWithCredentials,
    isLoggingInWithCredentials,
    loginWithCredentialsError,

    logout,
    resetUserScopedStores,
    isLoggingOut,
    logoutError,

    sendVerificationEmail,
    isSendingVerificationEmail,
    sendVerificationEmailError,
    lastAttemptedSendingVerificationEmail,

    sendForgotPasswordEmail,
    isSendingForgotPasswordEmail,
    sendForgotPasswordEmailError,
    lastAttemptedSendingForgotPasswordEmail,

    confirmResetPassword,
    isConfirmingResetPassword,
    confirmResetPasswordError,
    lastAttemptedConfirmingResetPassword,
  }
})
